import { lazy, Suspense } from 'react';
import { Route, Routes as ReactRoutes, useLocation } from 'react-router-dom';
import { FlowTypes } from 'store/constants';
import { PAGE_STATUS } from 'utils/constants';

import SkeletonSelectDefault from './components/Skeleton/select/SkeletonSelectDefault';
import DisplayFormLoader from './DisplayForm/components/DisplayFormLoader';
import { ModalContent } from './ModalContent/ModalContent';
import { modalRoutes } from './modalRoutes';
import { Routes } from './routes';

const WithdrawalMethod = lazy(() =>
  import('./Withdrawal/WithdrawalMethod').then((m) => ({
    default: m.WithdrawalMethod,
  })),
);
const DepositMethod = lazy(() =>
  import('./Deposit/DepositMethod').then((m) => ({
    default: m.DepositMethod,
  })),
);

const SavedCards = lazy(() =>
  import('./components/Cards/List').then((m) => ({
    default: m.SavedCards,
  })),
);
const VerifyAccountPage = lazy(() =>
  import('./Withdrawal/VerifyAccount/VerifyPage').then((m) => ({
    default: m.VerifyAccountPage,
  })),
);
const WithdrawalCardList = lazy(() =>
  import('./Withdrawal/Cards/List').then((m) => ({
    default: m.WithdrawalCardList,
  })),
);
const VerificationBlockedPage = lazy(() =>
  import('./Withdrawal/VerifyAccount/VerificationBlockedPage').then((m) => ({
    default: m.VerificationBlockedPage,
  })),
);
const Page3DSResult = lazy(() => import('./components/Page3DSResult'));

const DisplayFormPage = lazy(
  () =>
    import(
      /* webpackChunkName: "display-form" */
      './DisplayForm/DisplayFormPage'
    ),
);

const SuspendedDisplayFormPage = () => (
  <Suspense fallback={<DisplayFormLoader />}>
    <DisplayFormPage />
  </Suspense>
);

const DepositMethods = lazy(
  () =>
    import(
      /* webpackChunkName: "deposit-methods" */
      './Deposit/Methods/DepositMethods'
    ),
);

const WithdrawalMethods = lazy(
  () =>
    import(
      /* webpackChunkName: "withdrawal-methods" */
      './Withdrawal/Methods/WithdrawalMethods'
    ),
);

const StatusController = lazy(
  () =>
    import(
      /* webpackChunkName: "status-controller" */
      './Status/StatusController'
    ),
);

const CascadingController = lazy(() =>
  import(
    /* webpackChunkName: "cascading-controller" */
    './Cascading/CascadingController'
  ).then((module) => ({
    default: module.CascadingController,
  })),
);

export const RouterShell = () => {
  const location = useLocation();
  const state = location.state as { background?: Location } | undefined;

  return (
    <>
      <ReactRoutes location={state?.background || location}>
        <Route path={Routes.deposit} element={<DepositMethods />} errorElement={<h1>deposit failure</h1>}>
          {modalRoutes(`${Routes.deposit}/`)}
        </Route>
        <Route
          path={Routes.depositMethod}
          element={
            <Suspense fallback={<SkeletonSelectDefault />}>
              <DepositMethod />
            </Suspense>
          }
          errorElement={<h1>deposit failure</h1>}
        >
          {modalRoutes(`${Routes.depositMethod}/`)}
        </Route>
        <Route
          path={Routes.depositMethodForm}
          element={
            <Suspense fallback={<SkeletonSelectDefault />}>
              <DepositMethod />
            </Suspense>
          }
          errorElement={<h1>deposit failure</h1>}
        >
          {modalRoutes(`${Routes.depositMethodForm}/`)}
        </Route>
        <Route path={Routes.withdrawal} element={<WithdrawalMethods />} errorElement={<h1>withdrawal failure</h1>}>
          {modalRoutes(`${Routes.withdrawal}/`)}
        </Route>
        <Route
          path={Routes.withdrawalMethod}
          element={
            <Suspense fallback={<SkeletonSelectDefault />}>
              <WithdrawalMethod />
            </Suspense>
          }
          errorElement={<h1>withdrawal failure</h1>}
        >
          {modalRoutes(`${Routes.withdrawalMethod}/`)}
        </Route>
        <Route
          path={Routes.withdrawalCardList}
          element={<WithdrawalCardList />}
          errorElement={<h1>withdrawal failure</h1>}
        />
        <Route
          path={Routes.withdrawalVerifyAccount}
          element={<VerifyAccountPage />}
          errorElement={<h1>verify account failure</h1>}
        />
        <Route
          path={Routes.withdrawalSuccess}
          element={<StatusController status={PAGE_STATUS.success} type={FlowTypes.withdrawal} />}
          errorElement={<h1>Success page failure</h1>}
        />
        <Route
          path={Routes.withdrawalProcessing}
          element={<StatusController status={PAGE_STATUS.processing} type={FlowTypes.withdrawal} />}
          errorElement={<h1>Processing page failure</h1>}
        />
        <Route
          path={Routes.withdrawalError}
          element={<StatusController status={PAGE_STATUS.error} type={FlowTypes.withdrawal} />}
          errorElement={<h1>Error page failure</h1>}
        />
        <Route
          path={Routes.accountManagementBlockedPage}
          element={<VerificationBlockedPage />}
          errorElement={<h1>verify account failure</h1>}
        />
        <Route
          path={Routes.depositSuccess}
          element={<StatusController status={PAGE_STATUS.success} type={FlowTypes.deposit} />}
          errorElement={<h1>Success page failure</h1>}
        />
        <Route
          path={Routes.depositError}
          element={<StatusController status={PAGE_STATUS.error} type={FlowTypes.deposit} />}
          errorElement={<h1>Error page failure</h1>}
        />
        <Route
          path={Routes.depositProcessing}
          element={<StatusController status={PAGE_STATUS.processing} type={FlowTypes.deposit} />}
          errorElement={<h1>Processing page failure</h1>}
        />
        <Route path={Routes.cards} element={<SavedCards />} errorElement={<h1>Cards list page failure</h1>} />
        <Route path={Routes.page3dsResult} element={<Page3DSResult />} errorElement={<h1>3DS page failure</h1>} />
        <Route
          path={Routes.depositAwait}
          element={<StatusController status={PAGE_STATUS.await} type={FlowTypes.deposit} />}
          errorElement={<h1>Await page failure</h1>}
        />
        <Route
          path={Routes.cascading}
          element={<CascadingController />}
          errorElement={<h1>Cascading page failure</h1>}
        />
        <Route
          path={Routes.depositDisplayForm}
          element={<SuspendedDisplayFormPage />}
          errorElement={<h1>DisplayForm page failure</h1>}
        />
        <Route path="*" element={<DepositMethods />} errorElement={<h1>deposit failure</h1>} />
        {modalRoutes('/')}
      </ReactRoutes>
      {state?.background && (
        <ModalContent>
          <ReactRoutes>{modalRoutes('/')}</ReactRoutes>
        </ModalContent>
      )}
    </>
  );
};
